import {
  BuildingType,
  CounterTypes,
  ELProduct,
  HGLELProduct,
  HGLNGProduct,
  MeterUsage,
  NGProduct,
  Price,
  Product,
  SimulatePriceRequest,
  SimulatePriceResponse,
  Simulation,
  SimulationSteps
} from './types'
import { SimulationInputKeys, SimulationInputs } from 'store/simulation/types'
import { Region } from 'types/region'
import { updateSimulationInput } from 'store/simulation/actions/updateSimulationInput'
import { updateRegistrationInput } from 'store/registration/actions/updateRegistrationInputs'
import { saveParsedSimulationData } from 'store/simulation/actions/saveParsedSimulationData'
import { simulatePriceError, simulatePriceSuccess, startSimulation } from 'store/simulation/actions/simulatePrice'
import { checkPromoReferralCode, fetchPriceSimulation } from 'api/customerFlow'
import { updateAllSimulationInputs } from 'store/simulation/actions/updateAllSimulationInputs'
import { CheckPromoReferralCodeResponse } from 'types/promo-referral-codes'
import { InputGroupKeys, PersonalDataFieldKeys } from 'store/registration/types'
import { store } from 'store'
import { getFixedPricesForApiRequests } from 'utils/helpers'

/**
 * Adds a step after another step in the given simulation flow
 *
 * @param simulationFlow
 * @param stepToAdd
 * @param afterStep
 */
const addSimulationStepAfterGivenStep = (
  simulationFlow: SimulationSteps[],
  stepToAdd: SimulationSteps,
  afterStep: SimulationSteps
): SimulationSteps[] => {
  if (!simulationFlow.includes(stepToAdd)) {
    const index = simulationFlow.findIndex((value) => value === afterStep)
    simulationFlow.splice(index + 1, 0, stepToAdd).join()
  }

  return simulationFlow
}

/**
 * Calculates and saves the extra instalment based on the given isCompany, total & forcedAmount parameters
 *
 * @param {boolean} isCompany
 * @param {Price} total
 * @param {number} forcedAmount
 * @returns {number}
 */
const calculateAndSaveExtraInstalment = (isCompany: boolean, total: Price, forcedAmount: number) => {
  const priceWithPromo = Number(isCompany ? total.totalWithoutVAT : total.total)
  store.dispatch(updateSimulationInput({ key: SimulationInputKeys.EXTRA_INSTALMENT, value: forcedAmount - priceWithPromo }))
}

/**
 * Changes the simulation flow based on the given answer
 *
 * @param simulationFlow
 * @param key
 * @param inputs
 */
export const changeSimulationFlowBasedOnGivenAnswer = (
  simulationFlow: SimulationSteps[],
  key: SimulationInputKeys,
  inputs: SimulationInputs
): SimulationSteps[] => {
  switch (key) {
    // RESIDENTIAL OR BUSINESS
    case SimulationInputKeys.HAS_VAT:
      if (inputs[key] === false) {
        simulationFlow = addSimulationStepAfterGivenStep(simulationFlow, SimulationSteps.RESIDENTS, SimulationSteps.BUILDING_TYPE)
      } else {
        simulationFlow = removeStepFromSimulationFlow(simulationFlow, SimulationSteps.RESIDENTS)
      }
      break

    // SOLAR PANELS OR NOT
    case SimulationInputKeys.HAS_SOLAR_PANELS:
      if (inputs[key] === false) {
        simulationFlow = removeStepFromSimulationFlow(simulationFlow, SimulationSteps.SOLAR_POWER)
      } else {
        simulationFlow = addSimulationStepAfterGivenStep(simulationFlow, SimulationSteps.SOLAR_POWER, SimulationSteps.HAS_SOLAR_PANELS)
      }
      break

    // KNOWS USAGE OR NOT
    case SimulationInputKeys.KNOWS_USAGE:
      if (inputs[key] === true) {
        // Remove building type & residents steps
        simulationFlow = removeStepFromSimulationFlow(simulationFlow, SimulationSteps.BUILDING_TYPE)
        simulationFlow = removeStepFromSimulationFlow(simulationFlow, SimulationSteps.RESIDENTS)

        // Add usage step
        simulationFlow = addSimulationStepAfterGivenStep(simulationFlow, SimulationSteps.USAGE, SimulationSteps.METER_TYPE)
      } else {
        // Remove usage step
        simulationFlow = removeStepFromSimulationFlow(simulationFlow, SimulationSteps.USAGE)

        // Add building type & resident steps
        simulationFlow = addSimulationStepAfterGivenStep(simulationFlow, SimulationSteps.BUILDING_TYPE, SimulationSteps.METER_TYPE)

        if (inputs[SimulationInputKeys.HAS_VAT] === false)
          simulationFlow = addSimulationStepAfterGivenStep(simulationFlow, SimulationSteps.RESIDENTS, SimulationSteps.BUILDING_TYPE)
      }
      break

    default:
      break
  }

  return simulationFlow
}

/**
 * Checks if the usage is qualified as a residential usage
 *
 * @param usage
 * @param hasNightMeter
 * @param hasExclusiveNightMeter
 */
export const checkQualifiedUsage = (usage: MeterUsage, hasNightMeter: boolean, hasExclusiveNightMeter: boolean): boolean => {
  let valid = true

  if (!usage) return false

  if (usage.gas && usage.gas > 100000) valid = false

  let totalElectricityUsage = 0

  if (!hasNightMeter) {
    totalElectricityUsage = usage.singleRate ?? 0
  } else {
    totalElectricityUsage = (usage.doubleRate.day ?? 0) + (usage.doubleRate.night ?? 0)
  }

  if (hasExclusiveNightMeter) totalElectricityUsage += usage.exclNight ?? 0

  if (totalElectricityUsage > 50000) valid = false

  return valid
}

/**
 * Converts an electricity product to the correct Haugazel variant
 *
 * @param elProduct
 */
export const convertElProductToHaugazel = (elProduct: ELProduct): HGLELProduct | undefined => {
  switch (elProduct) {
    case ELProduct.Fixed:
      return HGLELProduct.Fixed
    case ELProduct.VariableOn:
      return HGLELProduct.VariableOn
    case ELProduct.VariableOff:
      return HGLELProduct.VariableOff
    case ELProduct.Go:
      return HGLELProduct.Go
    default:
      return undefined
  }
}

/**
 * Converts a gas product to the correct Haugazel variant
 *
 * @param ngProduct
 */
export const convertNgProductToHaugazel = (ngProduct: NGProduct): HGLNGProduct | undefined => {
  switch (ngProduct) {
    case NGProduct.Fixed:
      return HGLNGProduct.Fixed
    case NGProduct.VariableOn:
      return HGLNGProduct.VariableOn
    case NGProduct.VariableOff:
      return HGLNGProduct.VariableOff
    case NGProduct.Go:
      return HGLNGProduct.Go
    default:
      return undefined
  }
}

/**
 * Fetches the price simulation through the API and stores it in the simulation store
 */
export const getPriceSimulation = async (forcedAmount?: number): Promise<SimulatePriceResponse> => {
  store.dispatch(startSimulation())

  try {
    // Prepare simulation data for request
    const parsedSimulation = parseSimulationData()

    // Save some pieces of parsed simulation data in store as they will be reused
    store.dispatch(saveParsedSimulationData({ data: parsedSimulation }))

    // Check if a discount code has been set and do the simulation first without it, so that we know the price before promo
    if (parsedSimulation.discountCode) {
      // Copy the parsed simulation without the discount code
      const { discountCode, ...parsedSimulationWithoutDiscountCode } = parsedSimulation

      // Fetch the prices without promo
      const pricesWithoutPromo = await fetchPriceSimulation(parsedSimulationWithoutDiscountCode)

      // Define the total price without promo and save it in the store
      const totalPriceWithoutPromo = parsedSimulation.isCompany ? pricesWithoutPromo.total.totalWithoutVAT : pricesWithoutPromo.total.total
      store.dispatch(updateSimulationInput({ key: SimulationInputKeys.PRICE_WITHOUT_PROMO, value: totalPriceWithoutPromo }))
    }

    // Fetch price simulation through API request
    const simulatedPrice = await fetchPriceSimulation(parsedSimulation)

    const { inputs } = store.getState().simulation

    // Change simulated price if lead has solar panels, lives in Flanders & has a digital meter
    const newSimulatedPrice = modifyPriceBasedOnRegionAndCounterType(simulatedPrice, inputs)

    // Save the simulated price
    store.dispatch(simulatePriceSuccess({ simulatedPrice: newSimulatedPrice }))

    // Calculate & save extra instalment if forcedAmount is given
    if (forcedAmount) {
      calculateAndSaveExtraInstalment(inputs.hasVat, newSimulatedPrice.total, forcedAmount)
    }

    // Return the simulated price
    return newSimulatedPrice
  } catch (e) {
    store.dispatch(simulatePriceError())
  }
}

/**
 * Fetch the price simulation after promo code validation
 */
export const getPriceSimulationAfterPromoCodeValidation = async (): Promise<void> => {
  const { didSimulation, inputs, price } = store.getState().simulation
  const { extraInstalment } = inputs

  // Calculate the price without the promo
  const priceWithoutPromo = price && didSimulation ? (inputs.hasVat ? price.total.totalWithoutVAT : price.total.total) : 0

  // Update the price without promo
  store.dispatch(updateSimulationInput({ key: SimulationInputKeys.PRICE_WITHOUT_PROMO, value: priceWithoutPromo }))

  // Get the simulation
  const simulatedPrice = await getPriceSimulation()
  const { total } = simulatedPrice

  // Calculate & store new extra instalment if it's not 0
  if (extraInstalment && extraInstalment !== 0) {
    const forcedAmount = extraInstalment + Number(priceWithoutPromo)
    calculateAndSaveExtraInstalment(inputs.hasVat, total, forcedAmount)
  }
}

/**
 * Returns next SimulationStep based on a given flow & the current step
 *
 * @param simulationFlow
 * @param current
 */
export const getNextSimulationStep = (simulationFlow: SimulationSteps[], current: SimulationSteps): SimulationSteps => {
  const currentIndex = simulationFlow.findIndex((step: SimulationSteps) => step === current)
  return currentIndex + 1 < simulationFlow.length ? simulationFlow[currentIndex + 1] : simulationFlow[currentIndex]
}

/**
 * Returns previous SimulationStep based on a given flow & the current step
 *
 * @param simulationFlow
 * @param current
 */
export const getPreviousSimulationStep = (simulationFlow: SimulationSteps[], current: SimulationSteps): SimulationSteps => {
  const currentIndex = simulationFlow.findIndex((step: SimulationSteps) => step === current)
  return currentIndex - 1 >= 0 ? simulationFlow[currentIndex - 1] : simulationFlow[currentIndex]
}

/**
 * Modifies the simulated price and adds injection if the lead has solar panels, lives in Flanders & has digital counter
 *
 * @param simulatedPrice
 * @param inputs
 */
export const modifyPriceBasedOnRegionAndCounterType = (
  simulatedPrice: SimulatePriceResponse,
  inputs: SimulationInputs
): SimulatePriceResponse => {
  // Create the new simulated price
  let newSimulatedPrice: SimulatePriceResponse = simulatedPrice

  // Lead has solar panels, lives in Flanders & has a digital counter
  if (inputs.hasSolarPanels && inputs.region === Region.FLANDERS && inputs.counterType === CounterTypes.DIGITAL) {
    // STEP 1: Change electricity fields
    const electricity = simulatedPrice.electricity

    // Calculate injection (850 = solar hours per year | 0.150 = euro per kwh | 0.66 = 66% injected or 33% self consumption)
    electricity.injection = (inputs.solarPower * 850 * 0.15 * 0.66) / 12

    // Subtract injection from total without VAT
    electricity.totalWithoutVAT -= electricity.injection

    // Check if company
    if (inputs.hasVat) {
      // Subtract injection vat from total vat
      electricity.vat -= electricity.injection * 0.21

      // Calculate new total
      electricity.total = electricity.totalWithoutVAT + electricity.vat
      // Else residential
    } else {
      // Subtract injection from total (residential customers don't pay VAT)
      electricity.total -= electricity.injection
    }

    // STEP 2: Change total fields
    const total = simulatedPrice.total
    const gas = simulatedPrice.gas

    // Calculate new totals by adding changed electricity values with gas values
    total.total = parseFloat(electricity.total.toString()) + (gas ? parseFloat(gas.total.toString()) : 0)
    total.totalWithoutVAT = parseFloat(electricity.totalWithoutVAT.toString()) + (gas ? parseFloat(gas.totalWithoutVAT.toString()) : 0)
    total.vat = parseFloat(electricity.vat.toString()) + (gas ? parseFloat(gas.vat.toString()) : 0)
    total.injection = electricity.injection

    // STEP 3: Set the new simulatedPrice
    newSimulatedPrice = {
      ...simulatedPrice,
      total: parseDecimals(total),
      electricity: parseDecimals(electricity)
    }
  }

  // Change prices if total is less than 20 euros
  if (Number(newSimulatedPrice.total.total < 20)) {
    const withoutVatMultiplier = 0.79

    // Set totals to 20 euros (and -21% for without VAT)
    newSimulatedPrice.total.total = 20
    newSimulatedPrice.total.totalWithoutVAT = newSimulatedPrice.total.total * withoutVatMultiplier

    // Set electricity totals to 10 or 20 euros based on needsGas (and -21% for without VAT)
    newSimulatedPrice.electricity.total = inputs.needsGas ? 10 : 20
    newSimulatedPrice.electricity.totalWithoutVAT = newSimulatedPrice.electricity.total * withoutVatMultiplier

    // Set gas totals to 10 euros if needed (and -21% for without VAT)
    if (inputs.needsGas && newSimulatedPrice.gas) {
      newSimulatedPrice.gas.total = 10
      newSimulatedPrice.gas.totalWithoutVAT = newSimulatedPrice.gas.total * withoutVatMultiplier
    }
  }

  // Return unchanged price if no solar panels, not in flanders or no digital counter
  return newSimulatedPrice
}

/**
 * Parse al decimals to a locale string in a given object
 *
 * @param obj
 */
const parseDecimals = (obj: any) => {
  const dec = obj

  Object.keys(dec).forEach((key) => {
    dec[key] = dec[key].toLocaleString('en', { useGrouping: false, minimumFractionDigits: 2, maximumFractionDigits: 2 })
  })

  return obj
}

/**
 * Returns the parsed simulation data for fetching the price simulation from the API
 */
export const parseSimulationData = (): SimulatePriceRequest => {
  // Fetch marketing & simulation from the store
  const { marketing, simulation } = store.getState()

  // Fetch the inputs from the simulation
  const { inputs, fixedPrices } = simulation

  // Define needed data
  const hasExclNightMeter = inputs.hasExclusiveNightMeter || false
  const solarPowerShouldBeZero =
    inputs.hasSolarPanels &&
    ((inputs.region === Region.FLANDERS && inputs.counterType === CounterTypes.DIGITAL) || inputs.region === Region.WALLONIA)

  // Define the products
  const elProduct =
    inputs.electricityProduct || (marketing.defaultElectricityProduct ? marketing.defaultElectricityProduct : ELProduct.VariableOn)
  const ngProduct = inputs.gasProduct || (marketing.defaultGasProduct ? marketing.defaultGasProduct : NGProduct.VariableOn)
  const product: Product = {
    electricity: convertElProductToHaugazel(elProduct),
    gas: convertNgProductToHaugazel(ngProduct)
  }

  // Save electricity & gas product in simulation store for later use
  store.dispatch(updateSimulationInput({ key: SimulationInputKeys.ELECTRICITY_PRODUCT, value: elProduct }))
  store.dispatch(updateSimulationInput({ key: SimulationInputKeys.GAS_PRODUCT, value: ngProduct }))
  store.dispatch(updateSimulationInput({ key: SimulationInputKeys.HAUGAZEL_PRODUCTS, value: product }))

  // Return SimulatePriceRequest data
  return {
    isCompany: inputs.hasVat,
    knowsUsage: inputs.knowsUsage,
    exclNight: hasExclNightMeter,
    needsGas: inputs.needsGas,
    region: inputs.region,
    counterType: inputs.counterType,
    hasSolarPanels: inputs.hasSolarPanels,
    solarKv: !solarPowerShouldBeZero ? inputs.solarPower || 0 : 0,
    product,
    meter: inputs.meterType,
    meterUsage: {
      singleRate: inputs.usage.singleRate || 0,
      exclNight: inputs.usage.exclNight || 0,
      gas: inputs.usage.gas || 0,
      doubleRate: {
        day: inputs.usage.doubleRate.day || 0,
        night: inputs.usage.doubleRate.night || 0
      }
    },
    usageType: {
      // Not a company
      ...(!inputs.hasVat &&
        !inputs.knowsUsage && {
          residential: {
            people: inputs.residents,
            house: inputs.buildingType
          }
        }),

      // A company
      ...(inputs.hasVat &&
        !inputs.knowsUsage && {
          commercial: inputs.buildingType
        })
    },
    deliveryPoint: inputs.deliveryPoint,
    ...(inputs.promoCode && {
      discountCode: inputs.promoCode
    }),
    ...(inputs.referralCode && {
      referralCode: inputs.referralCode
    }),
    fixedPrices: getFixedPricesForApiRequests(fixedPrices, marketing.simulationType),
    extraInstalment: inputs.extraInstalment || 0,
    simulationType: marketing.simulationType
  }
}

/**
 * Removes all occurences of a step from the given simulation flow & returns the flow
 *
 * @param simulationFlow
 * @param stepToRemove
 */
export const removeStepFromSimulationFlow = (simulationFlow: SimulationSteps[], stepToRemove: SimulationSteps): SimulationSteps[] => {
  return simulationFlow.filter((step: SimulationSteps) => step !== stepToRemove)
}

/**
 * Parses (& converts if necessary) a serialized simulation params and saves it as the simulation inputs
 *
 * @param serialized
 */
export const storeSimulationParams = async (simulationParams: string | Simulation): Promise<void> => {
  const inputs = typeof simulationParams === 'string' ? JSON.parse(simulationParams) : simulationParams
  const { hasVat } = inputs

  // If hasVat is true, also update registration store
  if (hasVat) {
    // Set isCompany in registration store
    store.dispatch(updateRegistrationInput({ group: InputGroupKeys.PERSONAL_DATA, key: PersonalDataFieldKeys.IS_COMPANY, value: true }))
  }

  // Check if serialized data still has old promo code
  if ('promoCode' in inputs) {
    // Fetch the promo code
    const { promoCode } = inputs

    // Check if the promo referral code is not null and check it in the API
    if (promoCode !== null) {
      const checkedCode: CheckPromoReferralCodeResponse = await checkPromoReferralCode(promoCode)

      if ('valid' in checkedCode && checkedCode.valid) {
        // Remove the valid prop (not needed anymore) with the rest operator
        const { valid, ...checkedCodeData } = checkedCode

        // Update the checked code in the store
        store.dispatch(updateSimulationInput({ key: SimulationInputKeys.PROMO_CODE, value: checkedCodeData }))
      }
    }
  }

  // Check if old params or new params
  // if isCompany is in the inputs then it's the old params and they should be converted
  if ('isCompany' in inputs) {
    // Convert the old inputs
    const newInputs = {
      region: inputs.region,
      hasVat: inputs.isCompany,
      hasSolarPanels: inputs.hasSolarPanels,
      counterType: CounterTypes.ANALOG,
      ...(inputs.hasSolarPanels && {
        solarPower: inputs.kva,
        ...('solarPanels' in inputs && { solarPanels: inputs.solarPanels })
      }),
      needsGas: inputs.needsGas,
      knowsUsage: inputs.knowsUsage,
      meterType: inputs.meter,
      hasExclusiveNightMeter: inputs.exclNight,
      buildingType: !inputs.knowsUsage ? transformOldBuildingType(inputs.isCompany ? inputs.building : inputs.housing) : null,
      residents:
        !inputs.knowsUsage && !inputs.isCompany
          ? typeof inputs.people === 'string'
            ? transformOldPeople(inputs.people)
            : inputs.people
          : null,
      usage: {
        singleRate: inputs.totalKwh,
        exclNight: inputs.exclNightKwh,
        gas: inputs.gasKwh,
        doubleRate: {
          day: inputs.dayKwh,
          night: inputs.nightKwh
        }
      },
      firstname: '',
      lastname: '',
      email: '',
      phone: '',
      permission: false,
      promoCode: inputs.promoCode,
      referralCode: inputs.referralCode,
      priceWithoutPromo: null,
      extraInstalment: inputs.extraInstalment,
      electricityProduct: inputs.elProduct,
      gasProduct: inputs.ngProduct,
      haugazelProducts: {
        electricity: convertElProductToHaugazel(inputs.elProduct),
        gas: convertNgProductToHaugazel(inputs.ngProduct)
      },
      deliveryPoint: {
        address: {
          postalCode: inputs.postalCode,
          townCode: inputs.townCode,
          townName: inputs.townName
        }
      },
      usageType: {}
    }

    store.dispatch(updateAllSimulationInputs({ inputs: newInputs as SimulationInputs }))
  } else {
    const transformedInputs = {
      ...inputs,
      buildingType: transformOldBuildingType(inputs.buildingType),
      residents: transformOldPeople(inputs.residents)
    }

    console.log(transformedInputs)

    store.dispatch(updateAllSimulationInputs({ inputs: transformedInputs }))
  }
}

/**
 * Transforms the old buildingType strings to the new values if necessary
 *
 * @param {string} buildingType
 */
const transformOldBuildingType = (buildingType: string) => {
  switch (buildingType) {
    case 'soho':
      return BuildingType.Soho

    case 'shop':
      return BuildingType.Shop

    case 'office':
      return BuildingType.Office

    case 'industry':
      return BuildingType.Industry

    case 'house':
      return BuildingType.House

    case 'appartment':
      return BuildingType.Apartment

    default:
      return buildingType
  }
}

/**
 * Transforms the old people strings to the new number values if necessary
 *
 * @param {string} people
 */
const transformOldPeople = (people: string) => {
  switch (people) {
    case 'one':
      return 1

    case 'two':
      return 2

    case 'three':
      return 3

    case 'four':
      return 4

    default:
      return people
  }
}
